import { Patient, PatientDependent, Provider } from '@app/graphql-types/Source'
import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { Theme as MuiTheme, createTheme } from '@mui/material/styles'
import { NavLink } from 'react-router-dom'
import parser from 'ua-parser-js'

import { hexToHsl, hexToRGB, luminance, updateHexLuminance } from '../lib/color'
import { getQueryParamValue } from '../lib/uri'
import { UserTrackingTypes, UserTypeNames } from './enums'

export const FONT_SIZES = [
  { label: 'Default', size: '1rem', value: 0 },
  { label: 'Bigger', size: '1.1875rem', value: 1 },
  { label: 'Biggest', size: '1.375rem', value: 2 },
]

export const getFontSize = () => {
  const htmlTag = document.querySelector('html')
  if (!htmlTag) {
    return FONT_SIZES[0]
  }
  const fontSize = htmlTag.style.fontSize
  return FONT_SIZES.find((s) => s.size === fontSize) || FONT_SIZES[0]
}

export const getUserTrackingType = (
  profile?: PatientDependent | Patient | Provider,
): UserTrackingTypes => {
  let type = UserTrackingTypes.PATIENT
  if (profile?.__typename === UserTypeNames.DEPENDENT) {
    type = UserTrackingTypes.DEPENDENT
  } else if (profile?.__typename === UserTypeNames.PROVIDER) {
    type = UserTrackingTypes.PROVIDER
  }
  return type
}

export const NAV_HEIGHT = 50
export const SIDEBAR_WIDTH = 300
export const TAB_BUTTON_HEIGHT = 40
export const PATIENT_INFO_TABBED_NAV_HEIGHT = NAV_HEIGHT + TAB_BUTTON_HEIGHT
export const ENCOUNTER_MANAGEMENT_PANEL_WIDTH = 360
export const PATIENT_INFO_PANEL_WIDTH = 360
export const ANNOTATION_SELECTION_CLASSNAME = 'annotation-selection'

export const globalStyles: any = (theme) => ({
  '*, *::before, *::after': {
    boxSizing: 'border-box',
  },
  '*::selection': {
    color: theme.background,
    background: hexToHsl(theme.primary, 55),
  },
  a: {
    color: 'inherit',
    textDecoration: 'none',
  },
  body: {
    backgroundColor: theme.background,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    margin: 0,
    MozOsxFontSmoothing: 'grayscale',
    overflow: 'hidden',
    padding: 0,
    position: 'relative',
    textRendering: 'optimizeLegibility',
    WebkitFontSmoothing: 'antialiased',
  },
  button: {
    cursor: 'pointer',
  },
  'button, input, select, textarea': {
    background: 'transparent',
    border: 0,
    borderRadius: 0,
    color: 'inherit',
    display: 'inline-block',
    font: 'inherit',
    margin: 0,
    outline: 0,
    padding: 0,
    position: 'relative',
    textTransform: 'none',
    verticalAlign: 'middle',
    WebkitAppearance: 'button',
  },
  'button[disabled]': {
    cursor: 'auto',
  },
  'h1, h2, h3, h4, h5, h6': {
    lineHeight: 1.5,
    margin: 0,
  },
  html: {
    font: 'normal 400 100% / 1.5 -apple-system, sans-serif',
    fontSize: '100%',
    overflow: 'hidden',
    WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
    WebkitTextSizeAdjust: '100%',
  },
  img: {
    border: 0,
    fontSize: '0.75em',
    outline: 0,
  },
  'input::-moz-focus-inner, button::-moz-focus-inner': {
    border: 0,
    padding: 0,
  },
  p: {
    margin: 0,
  },
  ul: {
    margin: 0,
    padding: 0,
  },
  [`.${ANNOTATION_SELECTION_CLASSNAME}`]: {
    borderRadius: 14,
    boxShadow: '0 0 10px 0 #23c6a7',
  },
})

export const ellipsis: any = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

export const hitArea: any = {
  bottom: 0,
  left: 0,
  position: 'absolute',
  right: 0,
  top: 0,
}

export const boldRolloverFix: any = {
  content: 'attr(title)',
  display: 'block',
  fontWeight: 'bold',
  height: 0,
  overflow: 'hidden',
  visibility: 'hidden',
}

const focusNoneStyle: any = {
  '&:focus': {
    border: 'none',
    outline: 'none',
  },
}
export const FocusNoneButton = styled.button(focusNoneStyle)
export const FocusNoneNavLink = styled(NavLink)(focusNoneStyle)

// previously in FormElementParts.tsx, importing stopped working
export const elementProps: any = {
  display: 'block',
  fontSize: 16,
  justifyContent: 'center',
  width: '100%',
}

// previously in FormElementParts.tsx, importing stopped working
export const labelWhenSmallProps: any = {
  '[icon] &': {
    left: -30,
  },
  fontSize: 13,
  fontWeight: 'bold',
  left: 0,
  top: -22,
}

export const linkStyles = {
  '&:hover': {
    cursor: 'pointer',
    textDecoration: 'none',
  },
  textDecoration: 'underline',
}

export const pulse = (hex, size) => {
  const rgb = hexToRGB(hex)
  return keyframes({
    '0%': { boxShadow: `0 0 0 0 rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.8)` },
    '70%': {
      boxShadow: `0 0 0 ${size / 3}px rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0)`,
    },
    '100%': { boxShadow: `0 0 0 0 rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0)` },
  })
}
export const spin = keyframes({
  '0%': { transform: 'rotate(0deg)' },
  '100%': { transform: 'rotate(360deg)' },
})

export const highlightFade = (hex: string) => {
  return keyframes({
    '0%': { backgroundColor: hex },
    '70%': { backgroundColor: hex },
    '100%': { backgroundColor: 'transparent' },
  })
}

export interface Theme extends MuiTheme {
  accent: {
    aquamarine: string
    basil: string
    dust: string
    emerald: string
    mint: string
    purple: string
    sapphire: string
  }
  activeBackgroundColor: string
  background: string
  backgroundHover: string
  border: string
  borderRadius: number
  borderRadiusPillButton: number
  borderRadiusSmall: number
  cardHeight: number
  cardHeightMobile: number
  cardHeightSmall: number
  ease: string
  error: string
  errorBackground: string
  errorIcon: string
  errorIconDark: string
  fontFamily: string
  grey: string
  greyDark: string
  greyLight: string
  greyMedium: string
  greyMediumLight: string
  hover: string
  lineHeight: {
    large: number
  }
  notificationBg: string
  padding: number
  paddingMobile: number
  paddingSmall: number
  primary: string
  primaryDark: string
  primaryLuminance: number
  provider: {
    darkBlue: string
    green: string
    lightBlue: string
  }
  selectedHover: string
  queueAlert: string
  queueStatus: string
  speed: string
  success: string
  text: string
  textLight: string
  warning: string
}

// TODO: name properties to be associated with the types of elements they style
// all colors within the site should be applied through this base theme
const base = {
  ...createTheme(),
  // accent colors are only for CMD branding at this time
  accent: {
    // from Figma Color Styles
    aquamarine: '#00B2FF',
    emerald: '#33CCC4',
    sapphire: '#0066FF',
    // from the old "accent" colors
    basil: '#23C6A7',
    dust: '#D5D5D5',
    mint: '#A1DD5E',
    // new purple color
    purple: '#71318E',
  },
  activeBackgroundColor: '#E6E6E6',
  background: '#FFF',
  backgroundHover: '#F2F2F2',
  border: '#CCCCCC',
  borderRadius: 14,
  borderRadiusPillButton: 200,
  borderRadiusSmall: 4,
  cardHeight: 120,
  cardHeightMobile: 148,
  cardHeightSmall: 48,
  ease: 'ease',
  error: '#FD1E00',
  errorBackground: '#FFD2CC',
  errorIcon: '#CC1800',
  errorIconDark: '#991200',
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  grey: '#666',
  greyDark: '#2C343C',
  greyLight: '#F8F8F8',
  greyMedium: '#E7E7E7',
  greyMediumLight: '#B3B3B3',
  hover: '#CCCCCC',
  lineHeight: {
    large: 24,
  },
  notificationBg: '#FF4B33',
  padding: 24,
  paddingMobile: 14,
  paddingSmall: 8,
  primary: '#666',
  primaryDark: updateHexLuminance('#666', -0.5),
  primaryLuminance: luminance(hexToRGB('#666')),
  provider: {
    darkBlue: '#006B99',
    green: '#4C7E1B',
    lightBlue: '#B6E9FE',
  },
  selectedHover: '#4D4D4F',
  queueAlert: '#27343D',
  queueStatus: '#004F78',
  speed: '0.333s',
  success: '#7FD22D',
  text: '#1A1A1A',
  textLight: '#FFF',
  warning: '#FFCD00',
}

export const getCurrentRootFontSize = (defaultRoot = 16) => {
  const htmlTag = document.querySelector('html')
  if (!htmlTag) {
    return defaultRoot
  }
  const styleAttr = htmlTag.getAttribute('style')
  if (!styleAttr) {
    return defaultRoot
  }
  const match = styleAttr.match(/(\d+)/)
  if (!match || !match.length) {
    return defaultRoot
  }
  return Number(match[0])
}

// MUI Component style overrides
const componentOverrides = {
  MuiLoadingButton: {
    styleOverrides: {
      root: {
        borderRadius: 100,
        fontWeight: 'bold',
        minWidth: 'auto',
        // @ts-ignore
        whiteSpace: 'nowrap',
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        marginLeft: 2,
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        '&.Mui-focused': {
          color: base.text,
        },
        color: base.text,
        fontWeight: 'bold',
        marginLeft: -12,
        marginTop: 12,
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        legend: {
          maxWidth: 0.01,
        },
      },
      root: {
        backgroundColor: base.background,
        color: base.text,
        marginTop: 24,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        fontWeight: 'bold',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        backgroundColor: base.greyLight,
        paddingBottom: 16,
        paddingTop: 16,
      },
      root: {
        paddingBottom: 5,
        paddingTop: 5,
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:nth-of-type(even)': {
          backgroundColor: base.greyLight,
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        width: '100%',
      },
      scrollButtons: {
        '&.Mui-disabled': {
          opacity: 0.2,
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& [type="date"]::-webkit-calendar-picker-indicator': {
          paddingRight: 0,
        },
        '& .MuiInputLabel-root': {
          color: base.text,
          fontWeight: 'bold',
          marginLeft: -12,
          marginTop: 12,
          '&.Mui-focused': {
            color: 'black',
          },
        },
        '& .MuiOutlinedInput-root': {
          backgroundColor: base.background,
          color: base.text,
          marginTop: 24,
          '& fieldset': {
            borderColor: base.border,
          },
          '&:hover fieldset': {
            borderColor: base.border,
          },
          '&.Mui-focused fieldset': {
            borderColor: base.border,
          },
        },
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: {
        '& a': linkStyles,
      },
    },
  },
}

const paletteOverrides = {
  error: {
    main: base.error,
  },
  info: {
    main: base.background,
  },
  primary: {
    main: '#1a1a1a',
  },
  success: {
    main: base.success,
  },
  tertiary: {
    contrastText: base.textLight,
    main: base.accent.aquamarine,
  },
  warning: {
    main: base.warning,
  },
}

export const themeQuery = getQueryParamValue('theme')
// luminance describes the amount of light that is emitted from the screen for
// a particular color and in this case is a float between 0 and 1. we use this
// to change styling based on how light/dark a color is. a value that is less
// than 0.1 is almost black and therefore doesn't work with darker rollover or
// active states for things. see StreamButton for implementation.
export const themeNormal = (primaryColor: string): Theme => {
  const hex = themeQuery || primaryColor
  const primary = hex.indexOf('#') === 0 ? hex : `#${hex}`
  const primaryDark = updateHexLuminance(primary, -0.5)
  const primaryLuminance = luminance(hexToRGB(primary))

  return {
    ...base,
    ...createTheme({
      // @ts-ignore
      components: componentOverrides,
      palette: {
        ...paletteOverrides,
        secondary: {
          main: primary,
        },
      },
    }),
    primary,
    primaryDark,
    primaryLuminance,
  }
}

export const themeHighContrast = (primaryColor: string): Theme => {
  const hex = themeQuery || primaryColor
  const primary = updateHexLuminance(
    hex.indexOf('#') === 0 ? hex : `#${hex}`,
    -0.5,
  )
  const primaryDark = updateHexLuminance(primary, -0.5)
  const primaryLuminance = luminance(hexToRGB(primary))
  return {
    ...base,
    ...createTheme({
      // @ts-ignore
      components: componentOverrides,
      palette: {
        ...paletteOverrides,
        secondary: {
          main: primary,
        },
      },
    }),
    primary,
    primaryDark,
    primaryLuminance,
  }
}
// @ts-ignore
const p = parser()
const forceMobileLayout = p && p.device && p.device.type === 'mobile'

export enum MQ {
  DESKTOP = '@media (min-width: 48em)', // 768 / 16 = 48em
  LARGE_DESKTOP = '@media (min-width: 68.75em)', // 1100 / 16 = 68.75em
  TABLET = '@media (min-width: 40em)', // 640 / 16 = 40em
  X_LARGE_DESKTOP = '@media (min-width: 93.75em)', // 1500 / 16 = 68.75em
  IPAD = '@media only screen and (min-device-width: 48em) and (max-device-width: 64em) and (-webkit-min-device-pixel-ratio: 1)',
  IPAD_LANDSCAPE = '@media only screen and (min-device-width: 48em) and (max-device-width: 64em) and (-webkit-min-device-pixel-ratio: 1) and (orientation: landscape)',
  IPAD_PORTRAIT = '@media only screen and (min-device-width: 48em) and (max-device-width: 64em) and (-webkit-min-device-pixel-ratio: 1) and (orientation: portrait)',
  IPHONE_X = '@media only screen and (min-device-width: 23.4375em) and (max-device-width: 51.3125em) and (-webkit-min-device-pixel-ratio: 3)',
  IPHONE_X_LANDSCAPE = '@media only screen and (min-device-width: 23.4375em) and (max-device-width: 51.3125em) and (-webkit-min-device-pixel-ratio: 3) and (orientation: landscape)',
  IPHONE_X_PORTRAIT = '@media only screen and (min-device-width: 23.4375em) and (max-device-width: 51.3125em) and (-webkit-min-device-pixel-ratio: 3) and (orientation: portrait)',
  IPHONE_5_SE = '@media only screen and (min-device-width: 20em) and (max-device-width: 35.5em) and (-webkit-min-device-pixel-ratio: 2)',
  IPHONE_5_SE_LANDSCAPE = '@media only screen and (min-device-width: 20em) and (max-device-width: 35.5em) and (-webkit-min-device-pixel-ratio: 2) and (orientation: landscape)',
  IPHONE_5_SE_PORTRAIT = '@media only screen and (min-device-width: 20em) and (max-device-width: 35.5em) and (-webkit-min-device-pixel-ratio: 2) and (orientation: portrait)',
  IPHONE_6_7_8 = '@media only screen and (min-device-width: 23.4375em) and (max-device-width: 41.6875em) and (-webkit-min-device-pixel-ratio: 2)',
  IPHONE_6_7_8_LANDSCAPE = '@media only screen and (min-device-width: 23.4375em) and (max-device-width: 41.6875em) and (-webkit-min-device-pixel-ratio: 2) and (orientation: landscape)',
  IPHONE_6_7_8_PORTRAIT = '@media only screen and (min-device-width: 23.4375em) and (max-device-width: 41.6875em) and (-webkit-min-device-pixel-ratio: 2) and (orientation: portrait)',
  IPHONE_6_7_8_PLUS = '@media only screen and (min-device-width: 25.875em) and (max-device-width: 46em) and (-webkit-min-device-pixel-ratio: 3)',
  IPHONE_6_7_8_PLUS_LANDSCAPE = '@media only screen and (min-device-width: 25.875em) and (max-device-width: 46em) and (-webkit-min-device-pixel-ratio: 3) and (orientation: landscape)',
  IPHONE_6_7_8_PLUS_PORTRAIT = '@media only screen and (min-device-width: 25.875em) and (max-device-width: 46em) and (-webkit-min-device-pixel-ratio: 3) and (orientation: portrait)',
}

export const addFontSizeStyles = (
  obj,
  content,
  mq,
  fontSize,
  setValue = true,
) => {
  if (!obj[mq]) {
    obj[mq] = {}
  }
  Object.keys(content).forEach((rule) => {
    const value = content[rule]
    const ruleArr = rule.split(', ')
    ruleArr.forEach((r) => {
      if (typeof value === 'object') {
        if (r.indexOf('&') > -1) {
          obj[mq][`html[style*="${fontSize}"] ${r}`] = setValue ? value : null
        } else {
          obj[mq][`html[style*="${fontSize}"] & ${r}`] = setValue ? value : null
        }
      } else {
        if (!obj[mq][`html[style*="${fontSize}"] &`]) {
          obj[mq][`html[style*="${fontSize}"] &`] = {}
        }
        obj[mq][`html[style*="${fontSize}"] &`][r] = setValue ? value : null
      }
    })
  })
  return obj
}

// TODO figure out what to do with this garbage ☝️ & 👇
// @media screen value
//   [style*="font-size: 1.25rem"] {
//   }
//   [style*="font-size: 1.25rem"] {
//   }

export const MQ_TABLET = (content: any): any => {
  const obj = {}
  FONT_SIZES.forEach((fontSize, index) => {
    if (index > 0) {
      addFontSizeStyles(obj, content, MQ.DESKTOP, fontSize.size)
      addFontSizeStyles(obj, content, MQ.TABLET, fontSize.size, false)
    } else {
      addFontSizeStyles(
        obj,
        content,
        MQ.TABLET,
        fontSize.size,
        !forceMobileLayout,
      )
    }
  })
  return obj
}

export default styled
